import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BuildIcon from "@mui/icons-material/Build";
import BoltIcon from "@mui/icons-material/Bolt";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import RefreshIcon from "@mui/icons-material/Refresh";
import PrintIcon from "@mui/icons-material/Print";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import TuneIcon from "@mui/icons-material/Tune";
import StarekContext from "../../../context/StarekContext";
import { CATEGORIES, TEMPLATE_LABELS, DEVICE_COLORS } from "./reportCatalog";
import {
    GET_REPORT_DEVICES,
    GET_REPORT_SHIFTS,
    GET_MAINTENANCE_SCHEDULES,
    GET_DOWNTIME_REASONS,
    GET_DOWNTIME_EVENTS,
    CREATE_DOWNTIME_EVENT,
    fetchDatapointsForRange,
    deviceTransitions,
    filterTransitionsByPart,
    deviceTargetUnits,
    bucketCountsByDay,
    shiftWindowOnDate,
    countInWindow,
    detectDowntime,
    energyTrend,
    maintenanceWindow,
    maintenanceStatus,
} from "./reportData";
import {
    ProductionKpiTemplate,
    HeatmapTemplate,
    BulletRows,
    TimelineTemplate,
    ParetoTemplate,
    MaintSummaryTemplate,
    ChecklistTemplate,
    TrendTemplate,
    DowntimeTable,
    EmptyState,
} from "./templates";
import "./phase1.css";

const CATEGORY_ICONS = {
    production: ShowChartIcon,
    downtime: WarningAmberIcon,
    maintenance: BuildIcon,
    energy: BoltIcon,
};

// Reporting Phase 1 (CPRN00117-867), real data:
// devices/shifts/parts/reasons via GraphQL, unit counts from Timestream
// datapoints (high→low transitions), downtime detected from signal state and
// telemetry silence, energy as the raw uncalibrated power index.
// Per ticket: parts terminology, no schedule action, no favourites strip.

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
};
const fmtTime = (sec) =>
    new Date(sec * 1000).toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });

export default function ReportsPhase1() {
    const navigate = useNavigate();
    const starekContext = useContext(StarekContext);
    const { lastUsedWorkspace, lastUsedPlant, auth, user, types, showError } = starekContext;
    const plantId = lastUsedPlant?.id ? parseInt(lastUsedPlant.id) : null;

    const [activeCat, setActiveCat] = useState(0);
    const [activeReport, setActiveReport] = useState(0);
    const category = CATEGORIES[activeCat];
    const report = category.reports[activeReport];

    // ---- Filters ----
    const [selectedDeviceIds, setSelectedDeviceIds] = useState(null); // null = all
    const [singleDate, setSingleDate] = useState(todayStr());
    const [rangeStart, setRangeStart] = useState(daysAgoStr(6));
    const [rangeEnd, setRangeEnd] = useState(todayStr());
    const [selectedShiftId, setSelectedShiftId] = useState("");
    const [selectedPart, setSelectedPart] = useState("");

    // ---- Reference data ----
    const { data: devicesData, loading: devicesLoading } = useQuery(GET_REPORT_DEVICES, {
        variables: {
            where: {
                plant_id: { equals: plantId },
                is_active: { equals: true },
            },
        },
        skip: !plantId,
    });
    const { data: shiftsData } = useQuery(GET_REPORT_SHIFTS, {
        variables: { plantId },
        skip: !plantId,
    });
    const { data: maintData } = useQuery(GET_MAINTENANCE_SCHEDULES, {
        variables: { plantId },
        skip: !plantId,
    });
    const { data: reasonsData } = useQuery(GET_DOWNTIME_REASONS, {
        variables: { plantId },
        skip: !plantId,
    });

    const allDevices = useMemo(() => devicesData?.shadowDevices || [], [devicesData]);
    const shifts = useMemo(() => shiftsData?.shifts || [], [shiftsData]);
    const reasons = reasonsData?.downtimeReasons || [];

    const devices = useMemo(
        () =>
            selectedDeviceIds === null
                ? allDevices
                : allDevices.filter((d) => selectedDeviceIds.includes(d.id)),
        [allDevices, selectedDeviceIds],
    );

    const partOptions = useMemo(() => {
        const names = new Set();
        allDevices.forEach((d) =>
            (d.jobSchedules || []).forEach((j) => j.part?.name && names.add(j.part.name)),
        );
        return [...names].sort();
    }, [allDevices]);

    // ---- Report time range (unix seconds) ----
    const usesSingleDate = report.filters.includes("date");
    const range = useMemo(() => {
        const nowSec = Math.floor(Date.now() / 1000);
        if (usesSingleDate) {
            const start = Math.floor(new Date(`${singleDate}T00:00:00`).getTime() / 1000);
            return { start, end: Math.min(start + 86400, nowSec) };
        }
        const start = Math.floor(new Date(`${rangeStart}T00:00:00`).getTime() / 1000);
        const end = Math.min(
            Math.floor(new Date(`${rangeEnd}T23:59:59`).getTime() / 1000),
            nowSec,
        );
        return { start, end };
    }, [usesSingleDate, singleDate, rangeStart, rangeEnd]);

    // ---- Datapoints ----
    const [datapoints, setDatapoints] = useState(null);
    const [loadingDatapoints, setLoadingDatapoints] = useState(false);
    const deviceKey = devices.map((d) => d.id).join(",");
    const needsDatapoints = !["maintSummary", "checklist"].includes(report.template);

    useEffect(() => {
        if (!needsDatapoints || !deviceKey || !lastUsedWorkspace || range.end <= range.start) return;
        let cancelled = false;
        setLoadingDatapoints(true);
        fetchDatapointsForRange({
            workspace: lastUsedWorkspace,
            authToken: auth,
            deviceIds: deviceKey.split(","),
            startTime: range.start,
            endTime: range.end,
        })
            .then((data) => {
                if (!cancelled) setDatapoints(data);
            })
            .catch((err) => {
                if (!cancelled) {
                    setDatapoints(null);
                    showError?.(`Could not load telemetry: ${err.message}`);
                }
            })
            .finally(() => !cancelled && setLoadingDatapoints(false));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsDatapoints, deviceKey, range.start, range.end]);

    // ---- Downtime (detected + persisted tags) ----
    const isDowntimeReport = ["timeline", "pareto"].includes(report.template);
    const { data: eventsData, refetch: refetchEvents } = useQuery(GET_DOWNTIME_EVENTS, {
        variables: {
            plantId,
            from: new Date(range.start * 1000).toISOString(),
            to: new Date(range.end * 1000).toISOString(),
        },
        skip: !plantId || !isDowntimeReport,
    });
    const [createDowntimeEvent, { loading: tagging }] = useMutation(CREATE_DOWNTIME_EVENT);

    const detectedEvents = useMemo(() => {
        if (!isDowntimeReport || !datapoints) return [];
        const persisted = eventsData?.downtimeEvents || [];
        const nameOf = (id) => allDevices.find((d) => d.id === id)?.name || id;
        const events = [];
        devices.forEach((device, di) => {
            detectDowntime(device, datapoints, types, range.start, range.end).forEach((e) => {
                // A persisted (tagged) event covering this interval wins.
                const match = persisted.find(
                    (p) =>
                        p.device_id === e.device_id &&
                        Math.abs(new Date(p.start_time).getTime() / 1000 - e.start) < 120,
                );
                events.push({
                    key: `${e.device_id}-${e.start}`,
                    device_id: e.device_id,
                    deviceName: nameOf(e.device_id),
                    deviceIndex: di,
                    start: e.start,
                    end: e.end,
                    duration: e.duration,
                    source: e.source,
                    startLabel: fmtTime(e.start),
                    endLabel: fmtTime(e.end),
                    windowLabel: `${fmtTime(e.start)} – ${fmtTime(e.end)}`,
                    reasonName: match?.reason?.name || null,
                    persistedId: match?.id || null,
                });
            });
        });
        return events.sort((a, b) => a.start - b.start);
    }, [isDowntimeReport, datapoints, devices, allDevices, types, range, eventsData]);

    const handleTagReason = async (event, reasonId) => {
        try {
            await createDowntimeEvent({
                variables: {
                    data: {
                        plant: { connect: { id: plantId } },
                        device: { connect: { id: event.device_id } },
                        reason: { connect: { id: reasonId } },
                        start_time: new Date(event.start * 1000).toISOString(),
                        end_time: new Date(event.end * 1000).toISOString(),
                        duration_seconds: event.duration,
                        source: event.source,
                        ...(user?.id
                            ? {
                                  created_by: { connect: { id: parseInt(user.id) } },
                                  updated_by: { connect: { id: parseInt(user.id) } },
                              }
                            : {}),
                    },
                },
            });
            refetchEvents();
        } catch (err) {
            // Apollo hides the server's GraphQL errors behind a generic
            // "status code 400" — dig the real message out for the toast.
            const gqlError =
                err.graphQLErrors?.[0]?.message ||
                err.networkError?.result?.errors?.[0]?.message ||
                err.message;
            console.error("Tag downtime failed:", err);
            showError?.(`Could not tag downtime: ${gqlError}`);
        }
    };

    // ---- Per-report computed view ----
    const view = useMemo(() => {
        const startMs = range.start * 1000;
        const endMs = range.end * 1000;
        const part = selectedPart || null;

        const productionRows = () =>
            devices.map((device) => {
                const transitions = filterTransitionsByPart(
                    deviceTransitions(device, datapoints, types),
                    device.jobSchedules,
                    part,
                );
                return {
                    name: device.name || device.id,
                    device,
                    transitions,
                    actual: transitions.length,
                    target: deviceTargetUnits(device, startMs, endMs),
                };
            });

        switch (report.template) {
            case "productionKpi": {
                const rows = productionRows();
                const totalActual = rows.reduce((s, r) => s + r.actual, 0);
                const totalTarget = rows.reduce((s, r) => s + r.target, 0);
                const hours = Math.max((range.end - range.start) / 3600, 1);
                const best = rows.reduce(
                    (a, b) => (b.actual > (a?.actual || 0) ? b : a),
                    null,
                );
                return {
                    kpis: [
                        { label: "Good Parts", value: totalActual.toLocaleString() },
                        {
                            label: "Target",
                            value: totalTarget ? totalTarget.toLocaleString() : "—",
                            sub: totalTarget
                                ? `${Math.round((totalActual / totalTarget) * 100)}% achieved`
                                : "No targets set",
                        },
                        {
                            label: "Run Rate",
                            value: `${Math.round(totalActual / hours)}/h`,
                            sub: `over ${Math.round(hours)}h`,
                        },
                        {
                            label: "Best Machine",
                            value: best ? best.actual.toLocaleString() : "—",
                            sub: best?.name,
                        },
                    ],
                    rows,
                };
            }
            case "shiftKpi": {
                const shift = shifts.find((s) => String(s.id) === String(selectedShiftId));
                const window = shift
                    ? shiftWindowOnDate(shift, new Date(`${singleDate}T00:00:00`))
                    : [startMs, endMs];
                // Target is pro-rated to the shift window, not the whole day,
                // so shift attainment is measured against the shift's share.
                const rows = productionRows().map((r) => ({
                    ...r,
                    actual: window ? countInWindow(r.transitions, window) : 0,
                    target: window
                        ? deviceTargetUnits(r.device, window[0], window[1])
                        : 0,
                }));
                const totalActual = rows.reduce((s, r) => s + r.actual, 0);
                const totalTarget = rows.reduce((s, r) => s + r.target, 0);
                return {
                    kpis: [
                        { label: "Good Parts", value: totalActual.toLocaleString() },
                        {
                            label: "Target",
                            value: totalTarget ? totalTarget.toLocaleString() : "—",
                            sub: totalTarget
                                ? `${Math.round((totalActual / totalTarget) * 100)}% achieved`
                                : "No targets set",
                        },
                        {
                            label: "Shift",
                            value: shift?.name || "All day",
                            sub: window
                                ? `${new Date(window[0]).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} – ${new Date(window[1]).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                                : "Shift does not run on this date",
                        },
                        {
                            label: "Machines",
                            value: rows.length,
                        },
                    ],
                    rows,
                };
            }
            case "heatmap": {
                const dayBuckets = bucketCountsByDay([], startMs, endMs);
                const rows = productionRows().map((r) => {
                    const days = bucketCountsByDay(r.transitions, startMs, endMs);
                    return {
                        name: r.name,
                        cells: days.map((d) => ({
                            label: d.label,
                            value: d.count,
                            display: String(d.count),
                        })),
                    };
                });
                return {
                    rows,
                    dayLabels: dayBuckets.map((d) => d.label),
                    title: selectedPart
                        ? `Parts produced per day — ${selectedPart}`
                        : "Parts produced per day",
                };
            }
            case "bullet":
                return { rows: productionRows() };
            case "trend": {
                const bucketSec = range.end - range.start > 2 * 86400 ? 86400 : 3600;
                const series = devices.map((device) => {
                    const t = energyTrend(device, datapoints, types, range.start, range.end, bucketSec);
                    return { name: device.name || device.id, points: t.buckets.map((b) => b.avg) };
                });
                const n = series[0]?.points.length || 0;
                const labels = [];
                for (let i = 0; i < n; i += Math.max(1, Math.floor(n / 7))) {
                    const sec = range.start + i * bucketSec;
                    labels.push(
                        new Date(sec * 1000).toLocaleDateString(undefined, {
                            day: "2-digit",
                            month: "short",
                            ...(bucketSec === 3600 ? { hour: "2-digit" } : {}),
                        }),
                    );
                }
                return {
                    series,
                    labels,
                    title: "Power index (raw analog value, 0–1023)",
                    note: "Uncalibrated raw signal — install/calibrate energy meters to report kWh.",
                };
            }
            case "energyHeatmap": {
                const daySec = 86400;
                const rows = devices.map((device) => {
                    const t = energyTrend(device, datapoints, types, range.start, range.end, daySec);
                    return {
                        name: device.name || device.id,
                        cells: t.buckets.map((b) => ({
                            value: b.avg,
                            display: b.avg == null ? "—" : Math.round(b.avg),
                        })),
                    };
                });
                const labels = [];
                for (let t = range.start; t < range.end; t += daySec) {
                    labels.push(
                        new Date(t * 1000).toLocaleDateString(undefined, { day: "2-digit", month: "short" }),
                    );
                }
                return {
                    rows,
                    dayLabels: labels,
                    title: "Average power index per day (raw 0–1023, lower is better)",
                    invert: true,
                };
            }
            case "maintSummary":
            case "checklist": {
                const schedules = (maintData?.maintenanceSchedules || []).filter(
                    (m) => selectedDeviceIds === null || selectedDeviceIds.includes(m.device_id),
                );
                const withStatus = schedules.map((m) => {
                    const status = maintenanceStatus(m);
                    const { from, to } = maintenanceWindow(m);
                    const windowLabel =
                        from && to
                            ? `${from.toLocaleDateString(undefined, { day: "2-digit", month: "short" })} – ${to.toLocaleDateString(undefined, { day: "2-digit", month: "short" })}`
                            : "—";
                    return {
                        key: m.id,
                        title: m.name,
                        meta: `${m.device?.name || m.device_id}${m.description ? ` • ${m.description}` : ""}`,
                        window: windowLabel,
                        status,
                        statusLabel:
                            status === "upcoming"
                                ? "Upcoming"
                                : status === "in-progress"
                                    ? "In progress"
                                    : status === "past"
                                        ? "Window passed"
                                        : "Unscheduled",
                        statusClass:
                            status === "upcoming" ? "due" : status === "in-progress" ? "progress" : status === "past" ? "done" : "",
                        scheduleType: m.schedule_type,
                        from,
                    };
                });
                if (report.template === "checklist") {
                    const planned = withStatus
                        .filter((m) => m.scheduleType === 2)
                        .sort((a, b) => (a.from?.getTime() || 0) - (b.from?.getTime() || 0));
                    return { items: planned };
                }
                return {
                    stats: [
                        { label: "Total Schedules", value: withStatus.length },
                        {
                            label: "Upcoming",
                            value: withStatus.filter((m) => m.status === "upcoming").length,
                        },
                        {
                            label: "In Progress",
                            value: withStatus.filter((m) => m.status === "in-progress").length,
                        },
                        {
                            label: "Window Passed",
                            value: withStatus.filter((m) => m.status === "past").length,
                            sub: "No completion tracking yet",
                        },
                    ],
                    rows: withStatus.sort(
                        (a, b) => (a.from?.getTime() || 0) - (b.from?.getTime() || 0),
                    ),
                };
            }
            case "pareto": {
                const tagged = detectedEvents.filter((e) => e.reasonName);
                const untaggedSecs = detectedEvents
                    .filter((e) => !e.reasonName)
                    .reduce((s, e) => s + e.duration, 0);
                const byReason = {};
                tagged.forEach((e) => {
                    byReason[e.reasonName] = (byReason[e.reasonName] || 0) + e.duration;
                });
                if (untaggedSecs > 0) byReason["Untagged"] = untaggedSecs;
                const total = Object.values(byReason).reduce((s, v) => s + v, 0);
                let cum = 0;
                const buckets = Object.entries(byReason)
                    .sort((a, b) => b[1] - a[1])
                    .map(([label, seconds]) => {
                        const pct = Math.round((seconds / total) * 100);
                        cum += pct;
                        return { label, seconds, pct, cumPct: Math.min(cum, 100) };
                    });
                return { buckets: total > 0 ? buckets : [], totalSeconds: total };
            }
            default:
                return {};
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        report.template,
        devices,
        datapoints,
        types,
        range,
        selectedPart,
        selectedShiftId,
        singleDate,
        shifts,
        maintData,
        selectedDeviceIds,
        detectedEvents,
    ]);

    // ---- UI helpers ----
    const catStyle = useMemo(() => ({ "--cat-color": category.color }), [category.color]);
    const selectCat = (i) => {
        setActiveCat(i);
        setActiveReport(0);
    };
    const toggleDevice = (id) => {
        const current = selectedDeviceIds === null ? allDevices.map((d) => d.id) : selectedDeviceIds;
        if (current.includes(id)) {
            if (current.length === 1) return;
            setSelectedDeviceIds(current.filter((d) => d !== id));
        } else {
            setSelectedDeviceIds([...current, id]);
        }
    };
    const allChecked = selectedDeviceIds === null || selectedDeviceIds.length === allDevices.length;

    const rangeLabel = usesSingleDate
        ? new Date(`${singleDate}T00:00:00`).toLocaleDateString(undefined, {
              day: "2-digit",
              month: "short",
              year: "numeric",
          })
        : `${new Date(`${rangeStart}T00:00:00`).toLocaleDateString(undefined, { day: "2-digit", month: "short" })} – ${new Date(`${rangeEnd}T00:00:00`).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}`;

    const Icon = CATEGORY_ICONS[category.key];
    const loading = devicesLoading || (needsDatapoints && loadingDatapoints);

    const canvasBody = () => {
        if (loading) return <EmptyState>Loading report data…</EmptyState>;
        if (!allDevices.length) return <EmptyState>No devices in this plant yet.</EmptyState>;
        switch (report.template) {
            case "productionKpi":
            case "shiftKpi":
                return <ProductionKpiTemplate kpis={view.kpis || []} rows={view.rows || []} />;
            case "heatmap":
            case "energyHeatmap":
                return (
                    <HeatmapTemplate
                        rows={view.rows || []}
                        dayLabels={view.dayLabels || []}
                        title={view.title}
                        invert={view.invert}
                    />
                );
            case "bullet":
                return <BulletRows rows={view.rows || []} />;
            case "timeline":
                return (
                    <TimelineTemplate
                        events={detectedEvents}
                        deviceNames={devices.map((d) => d.name || d.id)}
                        reasons={reasons}
                        onTag={handleTagReason}
                        tagging={tagging}
                    />
                );
            case "pareto":
                return <ParetoTemplate buckets={view.buckets || []} totalSeconds={view.totalSeconds || 0} />;
            case "maintSummary":
                return <MaintSummaryTemplate stats={view.stats || []} rows={view.rows || []} />;
            case "checklist":
                return <ChecklistTemplate items={view.items || []} />;
            case "trend":
                return (
                    <TrendTemplate
                        series={view.series || []}
                        labels={view.labels || []}
                        title={view.title}
                        note={view.note}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="rp1 tw-px-6 tw-py-5" style={catStyle}>
            <div className="rp1-page-head">
                <div>
                    <h1>Reports</h1>
                    <p>
                        Pick a category, choose a report, apply your filters — every STAREK
                        report, in one place, ready to view or export.
                    </p>
                </div>
                <div className="tw-flex tw-gap-2">
                    <button className="rp1-btn rp1-btn-outline" onClick={() => navigate("/fav-reports")}>
                        <StarBorderIcon fontSize="small" /> Favourite Reports
                    </button>
                </div>
            </div>

            <div className="rp1-cat-tabs">
                {CATEGORIES.map((c, i) => {
                    const CIcon = CATEGORY_ICONS[c.key];
                    return (
                        <div
                            key={c.key}
                            className={`rp1-cat-tab ${i === activeCat ? "active" : ""}`}
                            style={i === activeCat ? { "--cat-color": c.color } : undefined}
                            onClick={() => selectCat(i)}
                        >
                            <CIcon fontSize="small" />
                            {c.label}
                        </div>
                    );
                })}
            </div>

            <div className="rp1-report-chips">
                {category.reports.map((r, i) => (
                    <div
                        key={r.id}
                        className={`rp1-chip ${i === activeReport ? "active" : ""}`}
                        onClick={() => setActiveReport(i)}
                    >
                        {r.name}
                    </div>
                ))}
            </div>

            <div className="rp1-grid">
                <div className="rp1-card">
                    <div className="rp1-card-head rp1-accent-head">
                        <h3><TuneIcon fontSize="small" /> Filters</h3>
                    </div>
                    <div className="rp1-card-body">
                        {report.filters.includes("devices") && (
                            <div className="rp1-f-group">
                                <label>Devices & Installations</label>
                                <div className="rp1-device-list">
                                    <div
                                        className={`rp1-device-item ${allChecked ? "checked" : ""}`}
                                        onClick={() => setSelectedDeviceIds(null)}
                                    >
                                        <input type="checkbox" checked={allChecked} readOnly />
                                        All Devices
                                    </div>
                                    {allDevices.map((d, i) => {
                                        const checked =
                                            selectedDeviceIds === null || selectedDeviceIds.includes(d.id);
                                        return (
                                            <div
                                                key={d.id}
                                                className={`rp1-device-item ${checked ? "checked" : ""}`}
                                                onClick={() => toggleDevice(d.id)}
                                            >
                                                <input type="checkbox" checked={checked} readOnly />
                                                <span
                                                    className="rp1-device-dot"
                                                    style={{ background: DEVICE_COLORS[i % DEVICE_COLORS.length] }}
                                                />
                                                <span className="tw-truncate">{d.name || d.id}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {usesSingleDate ? (
                            <div className="rp1-f-group">
                                <label>Select Date</label>
                                <input
                                    type="date"
                                    value={singleDate}
                                    max={todayStr()}
                                    onChange={(e) => setSingleDate(e.target.value)}
                                />
                            </div>
                        ) : report.filters.includes("daterange") ? (
                            <div className="rp1-f-group">
                                <label>Date Range</label>
                                <div className="rp1-f-row2">
                                    <div>
                                        <input
                                            type="date"
                                            value={rangeStart}
                                            max={rangeEnd}
                                            onChange={(e) => setRangeStart(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <input
                                            type="date"
                                            value={rangeEnd}
                                            max={todayStr()}
                                            onChange={(e) => setRangeEnd(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        {report.filters.includes("shift") && (
                            <div className="rp1-f-group">
                                <label>Shift</label>
                                <select
                                    value={selectedShiftId}
                                    onChange={(e) => setSelectedShiftId(e.target.value)}
                                >
                                    <option value="">All day</option>
                                    {shifts.map((s) => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {report.filters.includes("part") && (
                            <div className="rp1-f-group">
                                <label>Part</label>
                                <select
                                    value={selectedPart}
                                    onChange={(e) => setSelectedPart(e.target.value)}
                                >
                                    <option value="">All Parts</option>
                                    {partOptions.map((p) => (
                                        <option key={p} value={p}>{p}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                <div className="rp1-card">
                    <div className="rp1-card-head rp1-accent-head">
                        <h3>
                            <Icon fontSize="small" style={{ color: category.color }} />
                            {report.name}
                            <span className="rp1-view-badge">{TEMPLATE_LABELS[report.template]}</span>
                        </h3>
                        <div className="rp1-icon-row">
                            <div
                                className="rp1-icon-btn"
                                title="Refresh"
                                onClick={() => window.location.reload()}
                            >
                                <RefreshIcon fontSize="inherit" />
                            </div>
                            <div className="rp1-icon-btn" title="Print" onClick={() => window.print()}>
                                <PrintIcon fontSize="inherit" />
                            </div>
                        </div>
                    </div>
                    <div className="rp1-canvas-desc">
                        <b>{report.name}</b> — {report.desc}
                    </div>
                    <div className="rp1-canvas-meta">
                        <span className="rp1-meta-pill">
                            <CalendarMonthIcon sx={{ fontSize: 12 }} /> {rangeLabel}
                        </span>
                        {report.filters.includes("devices") && (
                            <span className="rp1-meta-pill">
                                {allChecked
                                    ? "All devices"
                                    : `${devices.length} device${devices.length > 1 ? "s" : ""} selected`}
                            </span>
                        )}
                        {selectedPart && report.filters.includes("part") && (
                            <span className="rp1-meta-pill">
                                <FilterAltIcon sx={{ fontSize: 12 }} /> Part: {selectedPart}
                            </span>
                        )}
                    </div>
                    <div className="rp1-viz-shell">{canvasBody()}</div>
                    {report.template === "timeline" && !loading && (
                        <DowntimeTable events={detectedEvents} />
                    )}
                </div>
            </div>
        </div>
    );
}
